<script>
import * as m from '@app/paraglide/messages.js';
    import { preventDefault } from 'svelte/legacy';

import { SvelteToast } from '@zerodevx/svelte-toast';
import { toast } from '@zerodevx/svelte-toast';
const ipcRenderer = window.ipcRenderer;
let machineId = $state(""), activationCode = $state(""), errorMsg = $state(""), loading = $state(false);
let mode = $state("key"), licenseKey = $state("");
const supportEmail = "support@closedcaptioncreator.com";
const ACTIVATE_PAGE = "https://www.closedcaptioncreator.com/activate.html";

/* Licence server errors (https/v8LicenseApi.js) and token check reasons, as user-facing text. */
function licenseErrorText(code, info) {
    switch (code) {
        case "invalid_key":
        case "not_found":
            return m.license_error_not_found();
        case "revoked":
            return m.license_error_revoked({ email: supportEmail });
        case "machine_limit":
            return m.license_error_machine_limit();
        case "rate_limited":
            return m.license_error_rate_limited();
        case "network":
            return m.license_error_network();
        case "maintenance":
            return m.license_error_maintenance({ date: info?.maintenanceUntil || "", release: info?.releaseDate || "" });
        case "expired":
            return m.license_error_expired();
        default:
            return m.login_offline_activation_failed_support({ email: supportEmail });
    }
}

/* Store a verified code/token and restart into the editor. */
async function useCode(code) {
    const licenseInfo = await ipcRenderer.invoke("licenseCheck", code);
    if (licenseInfo.active) {
        toast.push(m.login_offline_activation_success_toast(), {classes: ['toast-success']});
        localStorage.setItem("cc-offline-license", code);
        window.location.reload();
        return true;
    }
    errorMsg = licenseErrorText(licenseInfo.reason, licenseInfo);
    toast.push(m.login_offline_activation_error_toast(), {classes: ['toast-danger']});
    return false;
}

async function activateWithKey() {
    loading = true;
    errorMsg = "";
    try {
        const res = await ipcRenderer.invoke("licenseApi", { action: "activate", licenseKey });
        if (res.error || !res.token) {
            errorMsg = licenseErrorText(res.error);
            return;
        }
        // Kept so Help → Licence can show machines and release this computer later.
        localStorage.setItem("cc-license-key", licenseKey.trim().toUpperCase());
        if (!(await useCode(res.token))) localStorage.removeItem("cc-license-key");
    } catch (err) {
        errorMsg = m.login_offline_activation_failed_with_error({ message: err.message });
    } finally {
        loading = false;
    }
}

function openActivatePage() {
    ipcRenderer.invoke("openExternal", ACTIVATE_PAGE);
}

async function getMachineId(){
    try {
        machineId = await ipcRenderer.invoke("getMachineId");
    } catch(err){
        console.log(err, err.message);
    }
}

async function activate(){
    loading = true;
    errorMsg = "";
    try {
        if (!(await useCode(activationCode.trim()))) {
            activationCode = "";
        }
    } catch(err){
        errorMsg = m.login_offline_activation_failed_with_error({ message: err.message });
        toast.push(m.login_offline_activation_failed_with_error({ message: err.message }), {classes: ['toast-`danger`']});
    } finally {
        loading = false;
    }    
}

function copyMachineId(){
    navigator.clipboard.writeText(machineId);
    toast.push(m.login_offline_machine_id_copied_toast(), {classes: ['toast-`success`']});
}

async function PasteActivationCode(){
    try {
        activationCode = await navigator.clipboard.readText();
    } catch(err){
        console.log(err.message);
    }
}

getMachineId();

/* A stored licence that stopped working at startup (Creator.svelte authCheck) says why. */
try {
    const previous = JSON.parse(sessionStorage.getItem("cc-license-reason") || "null");
    sessionStorage.removeItem("cc-license-reason");
    if (previous?.reason) errorMsg = licenseErrorText(previous.reason, previous);
} catch (err) {}
</script>

<SvelteToast options={{
    duration: 1500,
    pausable: true,
    dismissable: true,
    reversed: true,
    intro: { y: 64 },
}}/>

<div id="LoginScreen">
    <div class="container d-flex justify-content-center align-items-center LoginPageWrapper">
        <div class="col-md-5 col-sm-8 col-11 shadow LoginFormWrapper text-white rounded border-0">
            <div class="d-flex justify-content-center align-items-center m-3 mb-4">
                <img src="./cc-logo.png" width="200px" alt={m.app_logo_alt()} class="mb-1"/>
            </div>
            
            <div class="offline-badge text-center mb-4">
                <span class="badge">{m.login_offline_mode_badge()}</span>
            </div>
            
            <h3 class="text-center mb-3">{m.login_offline_activate_license_title()}</h3>

            <div class="mode-switch mb-4" role="tablist">
                <button type="button" role="tab" class:active={mode === "key"} aria-selected={mode === "key"} onclick={() => ((mode = "key"), (errorMsg = ""))}>
                    {m.license_mode_key()}
                </button>
                <button type="button" role="tab" class:active={mode === "code"} aria-selected={mode === "code"} onclick={() => ((mode = "code"), (errorMsg = ""))}>
                    {m.license_mode_code()}
                </button>
            </div>

            {#if errorMsg}
                <div class="alert alert-danger" role="alert">
                    {errorMsg}
                </div>
            {/if}

            {#if mode === "key"}
            <form onsubmit={preventDefault(activateWithKey)}>
                <div class="mb-3">
                    <label for="licenseKeyInput" class="form-label">{m.license_key_label()}</label>
                    <input type="text" class="form-control license-key" id="licenseKeyInput" placeholder="CCC-XXXXX-XXXXX-XXXXX-XXXXX" autocomplete="off" spellcheck="false" bind:value={licenseKey}>
                    <div class="form-text text-white-50 mt-2">{m.license_key_help()}</div>
                </div>
                <div class="d-grid gap-2 mb-4">
                    <button type="submit" class="btn btn-primary" disabled={!licenseKey.trim() || loading}>
                        {#if loading}
                            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {m.login_offline_activating_button()}
                        {:else}
                            {m.login_offline_activate_button()}
                        {/if}
                    </button>
                </div>
                <p class="small text-white-50 mb-4">{m.license_key_offline_hint()}
                    <button type="button" class="btn btn-link btn-sm p-0 align-baseline" onclick={() => (mode = "code")}>{m.license_mode_code()}</button>
                </p>
            </form>
            {:else}
            <form onsubmit={preventDefault(activate)}>
                <p class="small text-white-50 mb-3">{m.license_code_lifetime_hint()}
                    <button type="button" class="btn btn-link btn-sm p-0 align-baseline" onclick={openActivatePage}>closedcaptioncreator.com/activate</button>
                </p>

                <div class="mb-3">
                    <label for="machineIdInput" class="form-label">{m.login_offline_machine_id_label()}</label>
                    <div class="input-group">
                        <input type="text" class="form-control" disabled id="machineIdInput" value={machineId}>
                        <button type="button" class="btn btn-outline-light" onclick={copyMachineId}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/>
                                <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/>
                            </svg>
                            {m.login_offline_copy_button()}
                        </button>
                    </div>
                </div>
                
                <div class="mb-4">
                    <label for="activationCodeInput" class="form-label">{m.login_offline_activation_code_label()}</label>
                    <div class="input-group">
                        <input type="text" class="form-control" id="activationCodeInput" placeholder={m.login_offline_activation_code_placeholder()} bind:value={activationCode}>
                        <button type="button" class="btn btn-outline-light" onclick={PasteActivationCode}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                <path fill-rule="evenodd" d="M10 1.5a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-1Zm-5 0A1.5 1.5 0 0 1 6.5 0h3A1.5 1.5 0 0 1 11 1.5v1A1.5 1.5 0 0 1 9.5 4h-3A1.5 1.5 0 0 1 5 2.5v-1Zm-2 0h1v1H3a1 1 0 0 0-1 1V14a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V3.5a1 1 0 0 0-1-1h-1v-1h1a2 2 0 0 1 2 2V14a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V3.5a2 2 0 0 1 2-2Z"/>
                            </svg>
                            {m.login_offline_paste_button()}
                        </button>
                    </div>
                </div>
                
                <div class="d-grid gap-2 mb-4">
                    <button type="submit" class="btn btn-primary" disabled={!activationCode || loading}>
                        {#if loading}
                            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {m.login_offline_activating_button()}
                        {:else}
                            {m.login_offline_activate_button()}
                        {/if}
                    </button>
                </div>
            </form>
            {/if}

            {#if mode === "code"}
            <div class="instructions-section">
                <h6 class="mb-3">{m.login_offline_instructions_title()}</h6>
                <ol>
                    <li>{m.login_offline_instruction_1()}</li>
                    <li>{@html m.login_offline_instruction_2()}</li>        
                    <li>{m.login_offline_instruction_3()}</li>           
                    <li>{@html m.login_offline_instruction_4()}</li>
                    <li>{m.login_offline_instruction_5()}</li>
                    <li>{m.login_offline_instruction_6()}</li>
                </ol>
            </div>
            {/if}

            <small class="form-text text-center text-white small">
                {m.login_offline_support_text()}
            </small>
        </div>
    </div>
</div>

<style lang="css">
#LoginScreen {
    position: relative;
    height: 100vh;
    width: 100vw;
    overflow: hidden;
    color: #f0f0f0;
    font-family: 'Roboto', sans-serif;
    background: #000000;
    background: linear-gradient(154deg, rgba(0, 0, 0, 1) 0%, rgba(6, 71, 57, 1) 100%);
}

.LoginPageWrapper {
    height: 100vh;
    position: relative;
    z-index: 1;
}

.LoginFormWrapper {
    background: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(10px);
    border-radius: 15px;
    padding: 40px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
    max-height: 90vh;
    overflow-y: auto;
}

/* Offline Badge */
.offline-badge .badge {
    background: linear-gradient(135deg, #dc3545, #c82333);
    color: white;
    padding: 8px 20px;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 500;
    letter-spacing: 0.5px;
}

/* Licence key / activation code switch */
.mode-switch {
    display: flex;
    padding: 4px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.12);
}

.mode-switch button {
    flex: 1;
    border: 0;
    border-radius: 999px;
    padding: 8px 12px;
    background: transparent;
    color: rgba(240, 240, 240, 0.75);
    font-weight: 500;
    transition: background-color 0.2s ease, color 0.2s ease;
}

.mode-switch button.active {
    background: linear-gradient(135deg, #089264, #06734e);
    color: #fff;
}

.license-key {
    font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.btn-link {
    color: #3fd3a3;
}

/* Form Styling */
.form-label {
    color: #f0f0f0;
    font-weight: 500;
    margin-bottom: 0.5rem;
}

.form-control {
    background-color: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #f0f0f0;
    border-radius: 8px;
    padding: 12px 16px;
    transition: all 0.3s ease;
}

.form-control:focus {
    background-color: rgba(255, 255, 255, 0.15);
    border-color: #089264;
    box-shadow: 0 0 0 0.2rem rgba(8, 146, 100, 0.25);
    color: #f0f0f0;
}

.form-control::placeholder {
    color: rgba(240, 240, 240, 0.6);
}

.form-control:disabled {
    background-color: rgba(255, 255, 255, 0.05);
    color: rgba(240, 240, 240, 0.7);
    cursor: not-allowed;
}

/* Input Group */
.input-group .form-control {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
}

.input-group .btn {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    display: flex;
    align-items: center;
    gap: 6px;
}

/* Button Styling */
.btn-primary {
    background: linear-gradient(135deg, #089264, #06734e);
    border: none;
    border-radius: 8px;
    padding: 12px 24px;
    font-weight: 500;
    transition: all 0.3s ease;
}

.btn-primary:hover:not(:disabled) {
    background: linear-gradient(135deg, #009978, #00853c);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(8, 146, 100, 0.3);
}

.btn-primary:disabled {
    background: rgba(8, 146, 100, 0.4);
    transform: none;
    box-shadow: none;
    cursor: not-allowed;
}

.btn-outline-light {
    border-color: rgba(255, 255, 255, 0.3);
    color: #f0f0f0;
    transition: all 0.3s ease;
}

.btn-outline-light:hover {
    background-color: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
    color: #f0f0f0;
}

/* Alert Styling */
.alert {
    border-radius: 8px;
    border: none;
    padding: 12px 16px;
    margin-bottom: 1.5rem;
}

.alert-danger {
    background-color: rgba(220, 53, 69, 0.2);
    color: #ff6b6b;
    border-left: 4px solid #dc3545;
}

/* Instructions Section */
.instructions-section {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
    padding: 20px;
    margin-bottom: 20px;
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.instructions-section h6 {
    color: #f0f0f0;
    font-weight: 500;
}

.instructions-section ol {
    color: rgba(240, 240, 240, 0.8);
    padding-left: 20px;
    margin-bottom: 0;
}

.instructions-section li {
    margin-bottom: 8px;
    line-height: 1.5;
}

/* Loading Spinner */
.spinner-border-sm {
    width: 1rem;
    height: 1rem;
}

small {
    display: block;
    margin-top: 15px;
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.7);
}

/* Responsive */
@media (max-width: 768px) {
    .LoginFormWrapper {
        padding: 30px 20px;
        margin: 10px;
    }
    
    .form-control {
        font-size: 16px;
    }
}

/* Custom scrollbar */
.LoginFormWrapper::-webkit-scrollbar {
    width: 6px;
}

.LoginFormWrapper::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
}

.LoginFormWrapper::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
}

.LoginFormWrapper::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
}
</style>
    